import { Navigate, Route, BrowserRouter as Router, Routes } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SkeletonTheme } from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ProtectedRoute from "./routes/ProtectedRoute";
import { AppLayout } from "./layouts/AppLayout";
import { Login, Tickets, Users } from "./pages";
import { ApiAccessProvider, AuthProvider } from "@repo/providers";
import { DialogProvider, LayoutProvider, SidebarProvider } from "./providers";
import type { ApiAccessConfig } from "@repo/configs";
import { ClientPlatform } from "@repo/types/client";
import type { AuthProviderConfig } from "@repo/configs";
import type { UserPrincipal } from "@repo/types/identity";

export default function App() {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: 1,
        staleTime: 5 * 60 * 1000, // 5 minutes
      },
    },
  });

  const authConfig: AuthProviderConfig = {
    setUserPrincipal: async (userInfo: UserPrincipal | null) => {
      if (userInfo) {
        sessionStorage.setItem("userInfo", JSON.stringify(userInfo));
      } else {
        sessionStorage.removeItem("userInfo");
      }
    },
    getUserPrincipal: async () => {
      const storedUser = sessionStorage.getItem("userInfo");
      if (storedUser) {
        try {
          return JSON.parse(storedUser);
        } catch {
          return null;
        }
      }
      return null;
    },
  };

  const apiAccessConfig: ApiAccessConfig = {
    baseURL: import.meta.env.VITE_API_URL,
    refreshEndpoint: `${import.meta.env.VITE_API_URL}/account/refreshToken`,
    withCredentials: true,
    platform: ClientPlatform.DesktopWeb,
  };

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <DialogProvider>
          <LayoutProvider>
            <SidebarProvider>
              <Router>
                <AuthProvider config={authConfig}>
                  <ApiAccessProvider config={apiAccessConfig}>
                    <SkeletonTheme baseColor="#f3f3f3" highlightColor="#ecebeb">
                      <Routes>
                        {/* Default redirect */}
                        <Route
                          path="/"
                          element={<Navigate to="/login" replace />}
                        />

                        {/* Public routes */}
                        <Route path="/login" element={<Login />} />

                        {/* Protected routes */}
                        <Route element={<ProtectedRoute />}>
                          <Route element={<AppLayout />}>
                            <Route path="/tickets" element={<Tickets />} />
                            <Route path="/users" element={<Users />} />
                          </Route>
                        </Route>
                        {/* Catch-all */}
                        <Route
                          path="*"
                          element={<Navigate to="/login" replace />}
                        />
                      </Routes>
                    </SkeletonTheme>
                  </ApiAccessProvider>
                </AuthProvider>
              </Router>
            </SidebarProvider>
          </LayoutProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </DialogProvider>
      </QueryClientProvider>
    </>
  );
}
